import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  TextField,
  TableSortLabel,
  TablePagination,
  useTheme,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import {
  useGetFeatureList,
  useCreateFeature,
} from "../../hooks/api/useFeature";
import type { Feature } from "../../Graphql/types/feature";
import { getFeatureDetailPath } from "../../constants/routes";
import { adminSurface, adminTableRowSx } from "../../styles/adminUiTokens";
import MembershipColumnFilterField from "./MembershipColumnFilterField";
import {
  compareSortValues,
  hasActiveFilters,
  matchesStatusFilter,
  matchesTextFilter,
  type SortOrder,
} from "./utils/membershipTableUtils";

type OrderBy = "featureCode" | "name" | "description" | "active";
type Order = SortOrder;

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

type FeatureFieldFilters = {
  featureCode: string;
  name: string;
  description: string;
  status: string;
};

const EMPTY_FEATURE_FILTERS: FeatureFieldFilters = {
  featureCode: "",
  name: "",
  description: "",
  status: "",
};

function getSortValue(f: Feature, field: OrderBy): string | number {
  if (field === "featureCode") return f.featureCode;
  if (field === "name") return f.name;
  if (field === "description") return f.description ?? "";
  return f.isActive ? 1 : 0;
}

function filterAndSort(
  list: Feature[],
  filters: FeatureFieldFilters,
  orderBy: OrderBy,
  order: Order,
): Feature[] {
  const filtered = list.filter(
    (f) =>
      matchesTextFilter(f.featureCode, filters.featureCode) &&
      matchesTextFilter(f.name, filters.name) &&
      matchesTextFilter(f.description ?? "", filters.description) &&
      matchesStatusFilter(f.isActive, filters.status),
  );
  return filtered.sort((a, b) =>
    compareSortValues(
      getSortValue(a, orderBy),
      getSortValue(b, orderBy),
      order,
    ),
  );
}

const FeatureListTable: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [fieldFilters, setFieldFilters] = useState<FeatureFieldFilters>(
    EMPTY_FEATURE_FILTERS,
  );
  const [orderBy, setOrderBy] = useState<OrderBy>("featureCode");
  const [order, setOrder] = useState<Order>("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addFeatureCode, setAddFeatureCode] = useState("");
  const [addName, setAddName] = useState("");
  const [addDescription, setAddDescription] = useState("");

  const getFeatureList = useGetFeatureList();
  const createFeature = useCreateFeature();

  useEffect(() => {
    getFeatureList
      .mutateAsync({ activeOnly: false })
      .then((res) => {
        setFeatures(res.listFeatures?.featureList ?? []);
      })
      .catch(() => {});
  }, []);

  const handleAddSuccess = () => {
    getFeatureList
      .mutateAsync({ activeOnly: false })
      .then((res) => setFeatures(res.listFeatures?.featureList ?? []))
      .catch(() => {});
    setAddModalOpen(false);
    setAddFeatureCode("");
    setAddName("");
    setAddDescription("");
  };

  const handleCreateFeature = () => {
    if (!addFeatureCode.trim() || !addName.trim()) return;
    createFeature
      .mutateAsync({
        featureCode: addFeatureCode.trim(),
        name: addName.trim(),
        description: addDescription.trim() || undefined,
      })
      .then(() => handleAddSuccess())
      .catch(() => {});
  };

  const sorted = useMemo(
    () => filterAndSort(features, fieldFilters, orderBy, order),
    [features, fieldFilters, orderBy, order],
  );

  const updateFieldFilter = (key: keyof FeatureFieldFilters, value: string) => {
    setFieldFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (field: OrderBy) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const isLoading = getFeatureList.isPending && features.length === 0;
  const isEmpty = !isLoading && features.length === 0;
  const noSearchResults = !isEmpty && sorted.length === 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={0} sx={{ ...adminSurface, width: "100%" }}>
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {hasActiveFilters(fieldFilters) && (
            <Button
              size="small"
              color="inherit"
              onClick={() => setFieldFilters(EMPTY_FEATURE_FILTERS)}
            >
              Clear column filters
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="medium"
            startIcon={<Add />}
            onClick={() => setAddModalOpen(true)}
          >
            Add
          </Button>
        </Box>
        <Dialog
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Add feature</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
              <TextField
                label="Feature code"
                value={addFeatureCode}
                onChange={(e) => setAddFeatureCode(e.target.value)}
                fullWidth
                size="small"
                required
                placeholder="e.g. PREMIUM_ALL"
              />
              <TextField
                label="Name"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                fullWidth
                size="small"
                required
              />
              <TextField
                label="Description"
                value={addDescription}
                onChange={(e) => setAddDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAddModalOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateFeature}
              disabled={
                !addFeatureCode.trim() ||
                !addName.trim() ||
                createFeature.isPending
              }
            >
              {createFeature.isPending ? "Creating…" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === "light" ? "grey.50" : "grey.900",
                }}
              >
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "featureCode"}
                    direction={orderBy === "featureCode" ? order : "asc"}
                    onClick={() => handleSort("featureCode")}
                  >
                    Code
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "description"}
                    direction={orderBy === "description" ? order : "asc"}
                    onClick={() => handleSort("description")}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "active"}
                    direction={orderBy === "active" ? order : "asc"}
                    onClick={() => handleSort("active")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 48, py: 2 }} />
              </TableRow>
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === "light" ? "grey.100" : "grey.800",
                }}
              >
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.featureCode}
                    onChange={(v) => updateFieldFilter("featureCode", v)}
                    aria-label="Filter by code"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.name}
                    onChange={(v) => updateFieldFilter("name", v)}
                    aria-label="Filter by name"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.description}
                    onChange={(v) => updateFieldFilter("description", v)}
                    aria-label="Filter by description"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.status}
                    onChange={(v) => updateFieldFilter("status", v)}
                    placeholder="Active…"
                    aria-label="Filter by status"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" width="40%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="60%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="70%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))
              ) : isEmpty ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No features found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : noSearchResults ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No features match the current column filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((f, index) => (
                  <TableRow
                    key={f.featureCode}
                    hover
                    sx={{
                      ...adminTableRowSx(index),
                      cursor: "pointer",
                      "&:hover .row-arrow": { opacity: 1 },
                    }}
                    onClick={() =>
                      navigate(getFeatureDetailPath(f.featureCode))
                    }
                  >
                    <TableCell
                      sx={{
                        py: 2,
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      {f.featureCode}
                    </TableCell>
                    <TableCell sx={{ py: 2, fontWeight: 500 }}>
                      {f.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        color: "text.secondary",
                        maxWidth: { md: 360 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.description || "—"}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={f.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={f.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, width: 48 }}>
                      <ArrowForwardIos
                        className="row-arrow"
                        sx={{
                          fontSize: 14,
                          color: "primary.main",
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && !isEmpty && sorted.length > 0 && (
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: 1, borderColor: "divider" }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default FeatureListTable;
